import { type Filters, isNonNullable, safeAbsurd } from '@ipfabric/utils';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { pipe } from 'fp-ts/lib/pipeable';
import { formatRule } from './formatRule';
import {
  type GroupFilter,
  type RuleFilter,
  isGroupFilter,
} from './useAdvancedFilter';

const maybeFormatArrayRule =
  (ruleFilter: RuleFilter) =>
  (filters: Filters | undefined): Filters | undefined => {
    if (!filters || !ruleFilter.column) {
      return undefined;
    }

    if (ruleFilter.arrayOperator) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const key = Object.keys(filters)[0]!;

      return {
        [key]: [ruleFilter.arrayOperator, ...(filters as any)[key as any]],
      };
    }

    return filters;
  };

const maybeFormatObjectRule =
  (ruleFilter: RuleFilter) =>
  (filters: Filters | undefined): Filters | undefined => {
    if (!filters || !ruleFilter.column) {
      return undefined;
    }

    if (ruleFilter.column.parent) {
      return {
        [ruleFilter.column.parent]: [
          ruleFilter.column.key,
          ...(filters as any)[ruleFilter.column.key],
        ],
      };
    }

    return filters;
  };

export const formatRuleFilter = (
  filter: RuleFilter,
  ruleContext: RuleContext,
): Filters | undefined => {
  if (!filter.column) {
    return undefined;
  }

  const filterType = filter.column.filter.type;

  switch (filterType) {
    case 'number':
    case 'select':
    case 'text':
    case 'uptime':
    case 'ip':
    case 'ipv6':
    case 'boolean':
    case 'asn':
    case 'routing':
    case 'routingIpv6':
    case 'dateTime':
    case 'object':
    case 'labels':
      return pipe(
        formatRule[filterType](filter, ruleContext),
        maybeFormatObjectRule(filter),
        maybeFormatArrayRule(filter),
      );
    default:
      safeAbsurd(filterType, 'unsupported filter');
      return undefined;
  }
};

export const formatGroupFilter = (
  filter: GroupFilter,
  ruleContext: RuleContext,
): Filters | undefined => {
  const formattedFilter = {
    [filter.operator]: filter.filters
      .map((rule) =>
        isGroupFilter(rule)
          ? formatGroupFilter(rule, ruleContext)
          : formatRuleFilter(rule, ruleContext),
      )
      .filter(isNonNullable),
  };

  // Fixed noUncheckedIndexedAccess NIM-19408
  return formattedFilter[filter.operator]!.length > 0
    ? formattedFilter
    : undefined;
};
